/*
  Excel-Erzeugung für den Schichtplan V2 (exceljs).

  Modernes, luftiges Layout mit einem Arbeitsblatt "Schichtplan":
  dunkles Kopfband, nur feine horizontale Trennlinien (keine schweren
  Rasterlinien), farbige Einträge als Kacheln mit Rich-Text (Zeit fett,
  Bezeichnung darunter), fixierte Kopfzeile und optionale Legende.
*/

import { buildEntryPalette, getReadableTextColor, mixHexColors, normalizeHexColor } from "../exportColors";

const SHEET_NAME = "Schichtplan";
const FONT = "Aptos";

const INK = "FF1E293B"; // Titel-/Namenstext
const MUTED = "FF64748B"; // Meta-Text
const HEADER_BG = "FFF8FAFC"; // helle, schlichte Kopfzeile
const HEADER_TX = "FF334155"; // Wochentag im Kopf
const HEADER_SUB = "FF94A3B8"; // Datum im Kopf (gedämpft)
const HEADER_LINE = "FF94A3B8"; // Unterkante der Kopfzeile
const NAME_BG = "FFF8FAFC"; // Namensspalte
const WEEKEND_BG = "FFF4F6F9";
const BAND_BG = "FFFCFDFE";
const ROW_SEP = "FFE9EDF2"; // feine Zeilentrennung
const NAME_SEP = "FFDCE3EC"; // Trennung nach Namensspalte

function toArgb(hexColor, fallbackHex = "#F1F5F9") {
  return `FF${normalizeHexColor(hexColor, fallbackHex).slice(1)}`;
}

// Zeit leicht betont, Bezeichnung schlicht darunter — mehrere Einträge getrennt.
function buildEntryRichText(entries, textArgb) {
  const richText = [];
  entries.forEach((entry, index) => {
    const italic = Boolean(entry.isDraft);
    if (index > 0) richText.push({ text: "\n", font: { name: FONT, size: 4 } });

    if (entry.time) {
      richText.push({ text: entry.time, font: { name: FONT, size: 9, bold: true, italic, color: { argb: textArgb } } });
      if (entry.name) {
        richText.push({ text: `\n${entry.name}`, font: { name: FONT, size: 8.5, italic, color: { argb: textArgb } } });
      }
    } else {
      richText.push({ text: entry.name || entry.fullLabel, font: { name: FONT, size: 9, italic, color: { argb: textArgb } } });
    }

    if (entry.isDraft) {
      richText.push({ text: "  (Entwurf)", font: { name: FONT, size: 7.5, italic: true, color: { argb: textArgb } } });
    }
  });
  return { richText };
}

function uniqueSheetName(value, usedNames) {
  const base = String(value || SHEET_NAME)
    .replace(/[:\\/?*]/g, "-")
    .split("[")
    .join("-")
    .split("]")
    .join("-")
    .trim()
    .slice(0, 31) || SHEET_NAME;
  let candidate = base;
  let index = 2;
  while (usedNames.has(candidate.toLocaleLowerCase("de"))) {
    const suffix = ` (${index})`;
    candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    index += 1;
  }
  usedNames.add(candidate.toLocaleLowerCase("de"));
  return candidate;
}

function buildGridSheet(workbook, { model, settings, periodLabel, sheetName = SHEET_NAME }) {
  const dayCount = model.days.length;
  const lastColumn = dayCount + 1;
  const sheet = workbook.addWorksheet(sheetName, {
    views: [{ state: "frozen", xSplit: 1, ySplit: 4, showGridLines: false }],
    pageSetup: {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 },
    },
  });
  const monochrome = settings.colorMode === "mono";
  const dayColumnWidth = dayCount > 10 ? 13 : 21;

  sheet.columns = [{ width: 25 }, ...model.days.map(() => ({ width: dayColumnWidth }))];

  // --- Titelblock -------------------------------------------------------
  const titleRow = sheet.getRow(1);
  titleRow.height = 32;
  const titleCell = titleRow.getCell(1);
  titleCell.value = settings.title ? `${settings.title} · ${periodLabel}` : `Schichtplan · ${periodLabel}`;
  titleCell.font = { name: FONT, size: 16, bold: true, color: { argb: INK } };
  titleCell.alignment = { vertical: "middle" };
  sheet.mergeCells(1, 1, 1, lastColumn);

  const metaRow = sheet.getRow(2);
  metaRow.height = 18;
  const metaCell = metaRow.getCell(1);
  metaCell.value = [
    settings.subtitle || "",
    `${model.rows.length} Mitarbeitende`,
    `${model.stats.shiftCount} Schichten`,
    settings.includeAbsences ? `${model.stats.absenceCount} Absenzen` : "",
    settings.includeHolidays ? `${model.stats.holidayCount} Ferien` : "",
    model.stats.draftCount > 0 ? `${model.stats.draftCount} Entwürfe` : "",
  ]
    .filter(Boolean)
    .join("      ·      ");
  metaCell.font = { name: FONT, size: 10, color: { argb: MUTED } };
  metaCell.alignment = { vertical: "middle" };
  sheet.mergeCells(2, 1, 2, lastColumn);

  sheet.getRow(3).height = 7;

  // --- Kopfzeile (Tage) -------------------------------------------------
  const headerBorder = { bottom: { style: "thin", color: { argb: HEADER_LINE } } };
  const headerRow = sheet.getRow(4);
  headerRow.height = 32;

  const nameHeader = headerRow.getCell(1);
  nameHeader.value = "Mitarbeitende";
  nameHeader.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_BG } };
  nameHeader.font = { name: FONT, size: 10, color: { argb: HEADER_TX } };
  nameHeader.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
  nameHeader.border = headerBorder;

  model.days.forEach((day, index) => {
    const cell = headerRow.getCell(index + 2);
    const isHoliday = day.publicHolidayNames.length > 0;
    cell.value = {
      richText: [
        {
          text: day.weekdayShort.replace(".", ""),
          font: { name: FONT, size: 10, color: { argb: isHoliday ? "FFC2410C" : HEADER_TX } },
        },
        { text: `\n${day.dayMonthLabel}`, font: { name: FONT, size: 8.5, color: { argb: HEADER_SUB } } },
      ],
    };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_BG } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = headerBorder;
    if (isHoliday) cell.note = day.publicHolidayNames.join(", ");
  });

  // --- Datenzeilen ------------------------------------------------------
  const rowSepBorder = { bottom: { style: "thin", color: { argb: ROW_SEP } } };

  model.rows.forEach((row, rowIndex) => {
    const sheetRow = sheet.getRow(rowIndex + 5);
    const maxEntries = model.days.reduce(
      (maximum, day) => Math.max(maximum, (row.cellsByDayKey[day.key] || []).length),
      1
    );
    sheetRow.height = Math.max(26, maxEntries * 26);
    const banded = rowIndex % 2 === 1;

    const nameCell = sheetRow.getCell(1);
    nameCell.value = row.name;
    nameCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAME_BG } };
    nameCell.font = { name: FONT, size: 10, color: { argb: INK } };
    nameCell.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
    nameCell.border = {
      bottom: { style: "thin", color: { argb: ROW_SEP } },
      right: { style: "thin", color: { argb: NAME_SEP } },
    };

    model.days.forEach((day, dayIndex) => {
      const cell = sheetRow.getCell(dayIndex + 2);
      const entries = row.cellsByDayKey[day.key] || [];
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      cell.border = rowSepBorder;

      if (!entries.length) {
        const emptyFill = day.isWeekend ? WEEKEND_BG : banded ? BAND_BG : null;
        if (emptyFill) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: emptyFill } };
        return;
      }

      const palette = buildEntryPalette(entries[0].color, { monochrome });
      const fillColor = entries.length === 1 ? palette.body : mixHexColors(palette.body, "#FFFFFF", 0.4);
      const textArgb = toArgb(getReadableTextColor(fillColor), "#1E293B");

      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: toArgb(fillColor) } };
      cell.value = buildEntryRichText(entries, textArgb);
    });
  });

  if (settings.includeLegend && model.legend.length) {
    buildLegendBlock(sheet, { model, monochrome, startRow: model.rows.length + 6, lastColumn });
  }

  sheet.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: lastColumn } };
  return sheet;
}

function buildLegendBlock(sheet, { model, monochrome, startRow, lastColumn }) {
  const heading = sheet.getRow(startRow);
  heading.height = 22;
  heading.getCell(1).value = "Legende";
  heading.getCell(1).font = { name: FONT, size: 10.5, bold: true, color: { argb: INK } };
  heading.getCell(1).alignment = { vertical: "middle" };

  const swatchSpan = Math.min(3, lastColumn);

  model.legend.forEach((item, index) => {
    const rowNumber = startRow + 1 + index;
    const row = sheet.getRow(rowNumber);
    row.height = 22;
    const palette = buildEntryPalette(item.color, { monochrome });
    const textArgb = toArgb(getReadableTextColor(palette.body), "#1E293B");

    const swatch = row.getCell(1);
    swatch.fill = { type: "pattern", pattern: "solid", fgColor: { argb: toArgb(palette.body) } };
    swatch.value = item.timeLabel ? `${item.name} · ${item.timeLabel}` : item.name;
    swatch.font = { name: FONT, size: 9.5, color: { argb: textArgb } };
    swatch.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
    sheet.mergeCells(rowNumber, 1, rowNumber, swatchSpan);

    const countCell = row.getCell(swatchSpan + 1);
    countCell.value = `${item.count}×`;
    countCell.font = { name: FONT, size: 9.5, color: { argb: MUTED } };
    countCell.alignment = { vertical: "middle" };
  });
}

export function buildExcelFilename(periodLabel) {
  const periodPart = String(periodLabel || "")
    .trim()
    .replace(/[\\/:*?"<>|·]+/g, " ")
    .replace(/\s+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);
  return `Schichtplan_${periodPart || "Export"}.xlsx`;
}

export async function downloadShiftplanExcel({ model, sections, settings, periodLabel }) {
  const { default: ExcelJS } = await import("exceljs/dist/exceljs.min.js");

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "TimeTab";
  workbook.created = new Date();

  const exportSections = Array.isArray(sections) && sections.length
    ? sections
    : [{ id: "default", title: SHEET_NAME, model }];
  const usedNames = new Set();
  exportSections.forEach((section) => {
    buildGridSheet(workbook, {
      model: section.model,
      settings,
      periodLabel,
      sheetName: uniqueSheetName(section.title, usedNames),
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = buildExcelFilename(periodLabel);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
